import { ISignalRHubV2, SignalRHubV2 } from "@buzzeasy/shared-frontend-utilities/signalr";
import { PropsWithChildren, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import environmentConfig from "../../../environmentConfig";
import { selectAuthInfo } from "../../../redux/authSlice";
import { CustomerConnection } from "../../models/websiteVistitors";
import { StarConversationCommand, WebsiteVisitorsContext } from "./WebsiteVisitorsContext";
import { RawCustomerConnection, RawCustomerSiteInteraction, mapToInternalCustomerConnection, mapToInternalSiteInteraction } from "./WebsiteVisitorsContext.mappers";

const { apiBaseUrl } = environmentConfig;

export default function WebsiteVisitorsProvider({ children }: PropsWithChildren): ReactElement {
  const { token } = useSelector(selectAuthInfo);

  const [customerConnections, setCustomerConnections] = useState<CustomerConnection[]>([]);
  const hubRef = useRef<ISignalRHubV2<WebsiteVisitorsHub, WebsiteVisitorsHubClient>>(null);

  const initializeCustomerConnections = useCallback(
    (cs: RawCustomerConnection[]) => setCustomerConnections(cs.map(mapToInternalCustomerConnection)),
    [],
  );

  const updateSiteInteraction = useCallback(
    (clientId: string, rawSiteInteraction: RawCustomerSiteInteraction) => setCustomerConnections((curr) => {
      const siteInteraction = mapToInternalSiteInteraction(rawSiteInteraction);
      const isNewConnection = !curr.find(c => c.clientId === clientId);

      if (isNewConnection)
        return [...curr, { clientId, siteInteraction }];
      else
        return curr.map(c => c.clientId === clientId ? { ...c, siteInteraction } : c);
    }),
    [],
  );

  const removeCustomerConnection = useCallback(
    (clientId: string) => setCustomerConnections(curr => curr.filter(c => c.clientId !== clientId)),
    [],
  );

  useEffect(
    () => {
      const customerTrackingHub = SignalRHubV2.create<WebsiteVisitorsHub, WebsiteVisitorsHubClient>(`${apiBaseUrl}/customer-tracking/agents`, token);
      customerTrackingHub.on("OnAgentConnected", initializeCustomerConnections);
      customerTrackingHub.on("OnCustomerDisconnected", removeCustomerConnection);
      customerTrackingHub.on("OnSiteInteractionAddedOrUpdated", updateSiteInteraction);

      hubRef.current = customerTrackingHub;

      void customerTrackingHub.start();
      return () => { void customerTrackingHub.stop(); };
    },
    [initializeCustomerConnections, removeCustomerConnection, token, updateSiteInteraction],
  );

  const startConversation = useCallback(
    (cmd: StarConversationCommand) => hubRef.current?.sendStartConversation(cmd),
    [],
  );

  const contextValue = useMemo(
    () => ({
      customerConnections,
      startConversation,
    }),
    [customerConnections, startConversation],
  );

  return (
    <WebsiteVisitorsContext.Provider value={contextValue}>
      {children}
    </WebsiteVisitorsContext.Provider>
  );
}

interface WebsiteVisitorsHub {
  StartConversation(cmd: StarConversationCommand): void;
}

interface WebsiteVisitorsHubClient {
  OnAgentConnected(connections: RawCustomerConnection[]): void;
  OnCustomerDisconnected(clientId: string): void;
  OnSiteInteractionAddedOrUpdated(clientId: string, siteInteraction: RawCustomerSiteInteraction): void;
}
